import { useState, useCallback } from 'react';
import { apiClient } from '../api/client';

export interface Credential {
  id: string;
  label: string;
  system_type: string;
  tribunal_slug: string;
  url_patterns: string[];
  catalog_key?: string | null;
  holder_id?: string | null;
  holder_name?: string | null;
  active: boolean;
  created_at: string;
  login_masked?: string;
  password_masked?: string;
  totp_secret?: string;
  /** A credencial tem 2FA (o segredo só vem para quem pode revelá-lo). */
  has_totp?: boolean;
  created_by?: string;
}

export interface BulkUpdatePayload {
  credential_ids: string[];
  login_plaintext?: string;
  password_plaintext?: string;
  totp_secret_plaintext?: string;
}

export interface BulkUpdateResult {
  updated: string[];
  skipped: string[];
  fields_changed: string[];
}

export function useCredentials() {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchCredentials = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await apiClient.get<Credential[]>('/credentials');
      setCredentials(data);
      setError(null);
    } catch (err: any) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createCredential = async (payload: any) => {
    await apiClient.post('/credentials', payload);
    await fetchCredentials();
  };

  const updateCredential = async (id: string, payload: any) => {
    await apiClient.patch(`/credentials/${id}`, payload);
    await fetchCredentials();
  };

  const deleteCredential = async (id: string) => {
    await apiClient.delete(`/credentials/${id}`);
    await fetchCredentials();
  };

  const bulkUpdateCredentials = async (payload: BulkUpdatePayload): Promise<BulkUpdateResult> => {
    const result = await apiClient.patch<BulkUpdateResult>('/credentials/bulk', payload);
    await fetchCredentials();
    return result;
  };

  return {
    credentials,
    isLoading,
    error,
    fetchCredentials,
    createCredential,
    updateCredential,
    deleteCredential,
    bulkUpdateCredentials,
  };
}
